import React, { memo, Suspense } from "react";
import cx from "classnames";
import { blockItem, blockItemContent } from "./_styles/BlockItem";
import { printableBlockItem } from "../../../../utils/print";
import type { DashboardBlockModelT } from "../../../../schema/dashboard";
import { FullViewportButtonMarker } from "../../../../../../../ui/FullViewportButton";
import { exportWidgetTarget } from "../../../../../../../ui/ExportWidgetButton";
import { BlockRichText } from "./RichText";
import { BlockComment } from "./Comment";
import { BlockHeading } from "./Heading";
import { BlockPlot } from "./Plot";
import { BlockImage } from "./Image";
import { BlockDiagram } from "./Diagram";
import { BlockPreset } from "./Preset";
import type { Project } from "../../../../../../../api/useProjects";
import { useDashboardEditorContext } from "../../../../hook/context/editor";
import useDebouncedCallback from "../../../../../../../ui/lib/hooks/useDebouncedCallback";
import { RichText } from "../Editor/InlineEditor/RichText";
import t from "../../../../../../../i18n";
import { css } from "@linaria/core";
import { SimpleInlineTextEditor } from "../Editor/InlineEditor/Text";
import { BlockLoading } from "./Loading";

export const Block = memo(
  ({
    project,
    block,
    powerUnitId,
    editable,
  }: {
    project: Project;
    block: DashboardBlockModelT;
    powerUnitId?: string | null;
    editable: boolean;
  }) => {
    const editor = useDashboardEditorContext();

    const updateTextualBlock = useDebouncedCallback(
      (value: string) => {
        if (block.kind === "TEXT" || block.kind === "HEADING") {
          void editor?.updateBlock({ ...block, data: value });
        }
      },
      [block, editor],
      500,
    );

    return (
      <div
        className={cx(blockItem, printableBlockItem, block.kind, {
          [FullViewportButtonMarker]: block.kind === "PLOT" || block.kind === "DIAGRAM",
          [exportWidgetTarget]: block.kind === "PLOT" || block.kind === "DIAGRAM",
          readonly: !editable,
        })}
        onClick={editable ? () => editor?.focus(block) : undefined}
      >
        {block.kind === "TEXT" &&
          (editable ? (
            <RichText
              dashboardId={block.dashboardId}
              placeholder={t("project.dashboard.block.text.placeholder")}
              initialValue={block.data}
              className={cx("body1", overflowXAuto)}
              onUpdate={updateTextualBlock}
            />
          ) : (
            <BlockRichText block={block} className={blockItemContent} />
          ))}
        {block.kind === "COMMENT" && (
          <BlockComment block={block} project={project} isDashboardEditing={editable} />
        )}
        {block.kind === "HEADING" &&
          (editable ? (
            <SimpleInlineTextEditor
              dashboardId={block.dashboardId}
              placehoder={
                block.config.level === 2
                  ? t("project.dashboard.block.title.placeholder")
                  : block.config.level === 3
                    ? t("project.dashboard.block.subtitle.placeholder")
                    : t("project.dashboard.block.sub_subtitle.placeholder")
              }
              initialValue={block.data}
              className={`h${block.config?.level}`}
              ignoreLikeBreaks
              onUpdate={updateTextualBlock}
            />
          ) : (
            <BlockHeading block={block} className={blockItemContent} />
          ))}
        {block.kind === "PLOT" && (
          <Suspense fallback={<BlockLoading />}>
            <BlockPlot
              className={blockItemContent}
              block={block}
              project={project}
              allowFullscreen
              editable={editable}
            />
          </Suspense>
        )}
        {block.kind === "IMAGE" && (
          <BlockImage
            className={blockItemContent}
            block={block}
            projectName={project.name}
            editable={editable}
          />
        )}
        {block.kind === "DIAGRAM" && (
          <Suspense fallback={<BlockLoading />}>
            <BlockDiagram
              className={blockItemContent}
              project={project}
              block={block}
              allowFullscreen
              editable={editable}
            />
          </Suspense>
        )}
        {block.kind === "PRESET" && (
          <Suspense fallback={<BlockLoading />}>
            <BlockPreset
              projectId={project.id}
              projectTimezone={project.timezone}
              powerUnitId={powerUnitId}
              block={block}
            />
          </Suspense>
        )}
      </div>
    );
  },
);

const overflowXAuto = css`
  overflow-x: auto;
`;
